import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { Switch } from '@rudi/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useGrantCredit,
  usePatchTeamCap,
  usePatchTenantCap,
  usePatchUserCap,
  useSpend,
} from '../api/hooks';
import type { TeamSpendGroup, TenantSpendGroup, UserSpendRow } from '../api/types';

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'Capped') return 'destructive';
  if (status === 'Uncapped') return 'secondary';
  return 'outline';
}

/** A Rudi without team caps leaves the field out, and its team endpoint answers 404. */
function hasTeamCap(team: TeamSpendGroup): boolean {
  return team.id != null && team.cap_eur_override !== undefined;
}

function GrantCreditDialog({ user, onClose }: { user: UserSpendRow; onClose: () => void }) {
  const [amount, setAmount] = useState('10');
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState<'this_month' | 'permanent'>('this_month');
  const grant = useGrantCredit();

  const save = async () => {
    try {
      await grant.mutateAsync({
        user_id: user.user_id,
        amount_eur: parseInt(amount, 10),
        reason: reason.trim(),
        scope,
      });
      toast.success('Credit granted');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant credit</DialogTitle>
          <DialogDescription>{user.display_name ?? user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Amount (EUR)</Label>
            <Input type="number" min={1} value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Scope</Label>
            <Select value={scope} onValueChange={v => setScope(v as 'this_month' | 'permanent')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="permanent">Permanent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={grant.isPending || !reason.trim() || !amount}>
            {grant.isPending ? 'Granting…' : 'Grant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserCapDialog({ user, onClose }: { user: UserSpendRow; onClose: () => void }) {
  const [capDisabled, setCapDisabled] = useState(user.status === 'Uncapped');
  const [cap, setCap] = useState(
    user.effective_cap_eur != null ? String(user.effective_cap_eur) : ''
  );
  const [reason, setReason] = useState('');
  const patch = usePatchUserCap();

  const save = async () => {
    try {
      await patch.mutateAsync({
        userId: user.user_id,
        cap_disabled: capDisabled,
        cap_eur_override: capDisabled || !cap ? null : parseInt(cap, 10),
        reason: reason.trim(),
      });
      toast.success('Cap updated');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust cap</DialogTitle>
          <DialogDescription>{user.display_name ?? user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Disable cap (unlimited)</Label>
            <Switch checked={capDisabled} onCheckedChange={setCapDisabled} />
          </div>
          {!capDisabled && (
            <div className="space-y-2">
              <Label>Cap override (EUR)</Label>
              <Input
                type="number"
                min={1}
                value={cap}
                onChange={e => setCap(e.target.value)}
                placeholder="team or tenant cap"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={patch.isPending || !reason.trim()}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamCapDialog({
  team,
  teamId,
  onClose,
}: {
  team: TeamSpendGroup;
  teamId: string;
  onClose: () => void;
}) {
  const [cap, setCap] = useState(
    team.cap_eur_override != null ? String(team.cap_eur_override) : ''
  );
  const [reason, setReason] = useState('');
  const patch = usePatchTeamCap();

  const save = async () => {
    try {
      await patch.mutateAsync({
        teamId,
        cap_eur_override: cap ? parseInt(cap, 10) : null,
        reason: reason.trim(),
      });
      toast.success('Team cap updated');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Team cap — {team.name}</DialogTitle>
          <DialogDescription>
            Monthly cap for each member of the team. A personal cap beats it, and it beats the
            tenant cap. Leave it empty to use the tenant cap.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Cap per member (EUR)</Label>
            <Input
              type="number"
              min={1}
              value={cap}
              onChange={e => setCap(e.target.value)}
              placeholder="tenant cap"
            />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={patch.isPending || !reason.trim()}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TenantCapDialog({ tenant, onClose }: { tenant: TenantSpendGroup; onClose: () => void }) {
  const [capDisabled, setCapDisabled] = useState(tenant.cap_disabled);
  const [cap, setCap] = useState(
    tenant.cap_eur_override != null ? String(tenant.cap_eur_override) : ''
  );
  const [reason, setReason] = useState('');
  const patch = usePatchTenantCap();

  const save = async () => {
    try {
      await patch.mutateAsync({
        tenantId: tenant.id,
        cap_disabled: capDisabled,
        cap_eur_override: capDisabled || !cap ? null : parseInt(cap, 10),
        reason: reason.trim(),
      });
      toast.success('Tenant cap updated');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tenant cap — {tenant.name}</DialogTitle>
          <DialogDescription>
            Monthly cap for each member without a team or personal cap. Leave it empty for no cap.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Disable cap (unlimited)</Label>
            <Switch checked={capDisabled} onCheckedChange={setCapDisabled} />
          </div>
          {!capDisabled && (
            <div className="space-y-2">
              <Label>Cap override (EUR)</Label>
              <Input
                type="number"
                min={1}
                value={cap}
                onChange={e => setCap(e.target.value)}
                placeholder="no cap"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={patch.isPending || !reason.trim()}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SpendPage() {
  const { data, isLoading, error } = useSpend();
  const [grantUser, setGrantUser] = useState<UserSpendRow | null>(null);
  const [capUser, setCapUser] = useState<UserSpendRow | null>(null);
  const [capTenant, setCapTenant] = useState<TenantSpendGroup | null>(null);
  const [capTeam, setCapTeam] = useState<{ team: TeamSpendGroup; teamId: string } | null>(null);

  return (
    <div>
      <PageHeader
        title="Spend"
        description={
          data
            ? `Period resets ${new Date(data.period_resets_at).toLocaleDateString()}`
            : 'Monthly spend caps and credits. Nobody has a cap until one is set.'
        }
      />
      <QueryState isLoading={isLoading} error={error} empty={data?.tenants.length === 0}>
        <div className="space-y-8">
          {data?.tenants.map(tenant => {
            const rows = tenant.teams.flatMap(team =>
              team.users.map(u => ({ team: team.name, user: u }))
            );
            return (
              <section key={tenant.id}>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold">{tenant.name}</h2>
                    <p className="text-xs text-muted-foreground">
                      {tenant.tenant_member_count} members · €{tenant.tenant_spend_eur} this period
                      {tenant.cap_disabled && ' · cap disabled'}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCapTenant(tenant)}>
                    Tenant cap
                  </Button>
                </div>
                {tenant.teams.some(hasTeamCap) && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {tenant.teams.map(team => {
                      const teamId = team.id;
                      if (!teamId || !hasTeamCap(team)) return null;
                      return (
                        <Button
                          key={teamId}
                          variant="outline"
                          size="sm"
                          onClick={() => setCapTeam({ team, teamId })}
                        >
                          {team.name} cap
                          {team.cap_eur_override != null ? ` · €${team.cap_eur_override}` : ''}
                        </Button>
                      );
                    })}
                  </div>
                )}
                <div className="rounded-3xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Spend</TableHead>
                        <TableHead>Cap</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map(({ team, user }) => (
                        <TableRow key={user.user_id}>
                          <TableCell className="font-medium">
                            {user.display_name ?? user.email}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{team}</TableCell>
                          <TableCell>€{user.period_spend_eur}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {user.effective_cap_eur != null ? `€${user.effective_cap_eur}` : '∞'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {user.period_credits_eur ? `€${user.period_credits_eur}` : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(user.status)}>{user.status}</Badge>
                          </TableCell>
                          <TableCell className="space-x-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => setGrantUser(user)}>
                              Credit
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setCapUser(user)}>
                              Cap
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>
            );
          })}
        </div>
      </QueryState>

      {grantUser && <GrantCreditDialog user={grantUser} onClose={() => setGrantUser(null)} />}
      {capUser && <UserCapDialog user={capUser} onClose={() => setCapUser(null)} />}
      {capTenant && <TenantCapDialog tenant={capTenant} onClose={() => setCapTenant(null)} />}
      {capTeam && (
        <TeamCapDialog
          team={capTeam.team}
          teamId={capTeam.teamId}
          onClose={() => setCapTeam(null)}
        />
      )}
    </div>
  );
}
